import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  OnDestroy,
  Renderer2,
  RendererFactory2,
  signal,
} from '@angular/core';

import { MonitoringLogger } from '@fcom/core/services';
import { isTruthy } from '@fcom/core/utils/utils';
import { PageMetaService } from '@fcom/common/services';

import { ResponsiveImage, ResponsiveImageData, SrcSet, StaticImage } from '../interfaces';
import { FALLBACK_WIDTH, IMAGE_WIDTHS, THUMBNAIL_WIDTH } from '../constants';
import { AspectRatios, FetchPriorityEnum } from '../enums';
import { aspectRatio, buildUrl, getWidestImageWidth, isStaticImage } from '../utils';

/**
 * Displays an image with lazyloading and a blurred placeholder.
 * This component uses ng-lazyload-image to load the images.
 * If a ResponsiveImage is passed, then the component selects the image
 * with the corresponding ratio and uses a srcset to adjust the image width
 * based on screensize.
 *
 * @example
 * <fcom-image
 *   [alt]="alt
 *   [imageData]="imageData"
 *   [ratio]="ratio">
 * </fcom-image>
 */
@Component({
  selector: 'fcom-image',
  templateUrl: './image.component.html',
  styleUrls: ['./image.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ImageComponent implements OnDestroy {
  private logger = inject(MonitoringLogger, { optional: true });
  private pageMetaService = inject(PageMetaService);

  /**
   * text for the images alt attribute.
   */
  alt = input('');

  /**
   * Stretches image to fill the container.
   */
  fillParent = input(false);

  /**
   * Image data to use.
   * Can be ResponsiveImage data in which case the ratio to use should be set.
   * Or this can be staticImage data in which case no ratio is required.
   */
  imageData = input<ResponsiveImage | StaticImage>();

  /**
   * The preferred aspect ratio of the image. Defaults to 16x9
   */
  ratio = input<keyof typeof AspectRatios>('16x9');

  /**
   * Scale the picture size with transform or not. Defaults to 1.1 scale.
   */
  scale = input(true);

  maxWidthImage = input(IMAGE_WIDTHS[0]);

  /**
   * Define fetch priority of the image
   */
  fetchPriority = input<keyof typeof FetchPriorityEnum>();

  isLazyLoad = input(true);

  isDefaultImageLoaded = signal(false);

  renderer: Renderer2;

  private mappedImage = computed<ResponsiveImageData | null>(() => {
    const imageData = this.imageData();
    const ratio = this.ratio();
    if (!imageData || this.invalidResponsiveImageRatio(imageData, ratio)) {
      return null;
    }
    return this.mapToResponsiveImage(imageData, AspectRatios[ratio]);
  });

  thumbnailSrc = computed(() => this.mappedImage()?.thumbnailSrc);
  fallbackSrc = computed(() => this.mappedImage()?.fallbackSrc);
  srcSet = computed<SrcSet>(() => this.mappedImage()?.srcSet ?? []);
  hasAspectRatio = computed(() => this.mappedImage()?.hasAspectRatio ?? false);
  customAspectRatio = computed(() => (!this.fillParent() ? this.mappedImage()?.customAspectRatio : undefined));

  preloadImages = computed(() => {
    if (this.isLazyLoad() || !this.srcSet().length) {
      return null;
    }
    return this.srcSet()
      .map((source) => `${source.src} ${source.width}w`)
      .join(', ');
  });

  preloadImagesWidth = computed(() => {
    if (this.isLazyLoad() || !this.srcSet().length) {
      return null;
    }
    return this.srcSet()
      .map((source) => `(min-width: ${source.width}px) 100vw`)
      .join(', ');
  });

  constructor() {
    const rendererFactory = inject(RendererFactory2);
    this.renderer = rendererFactory.createRenderer(null, null);

    effect(() => {
      const imageData = this.imageData();
      if (!imageData || this.invalidResponsiveImageRatio(imageData, this.ratio())) {
        this.logger?.error(
          'Both imageData and ratio must be set if imageData is ResponsiveImage. If ratio is ORIGINAL, the properties data, width and height are required.'
        );
      }
    });

    effect(() => {
      const srcSet = this.srcSet();
      if (!this.isLazyLoad() && srcSet.length > 0) {
        srcSet.forEach((item) => {
          this.pageMetaService.movePreloadImageLinksToHead(this.renderer, item.src, item?.width);
        });
      }
    });
  }

  onImageLoaded(): void {
    this.isDefaultImageLoaded.set(true);
  }

  ngOnDestroy(): void {
    const srcSet = this.srcSet();
    if (!this.isLazyLoad() && srcSet.length > 0) {
      srcSet.forEach((item) => {
        this.pageMetaService.removePreloadImageLinksFromHead(this.renderer, item.src);
      });
    }
  }

  /**
   * Process the ImageData and ratio to get the image data to pass to the template
   */
  private mapToResponsiveImage(pic: ResponsiveImage | StaticImage, aspect: AspectRatios): ResponsiveImageData {
    if (isStaticImage(pic)) {
      return {
        fallbackSrc: pic.fullSizeUrl,
        thumbnailSrc: pic.thumbnailSrc,
        srcSet: [],
        hasAspectRatio: false,
      };
    }
    const images =
      aspect === AspectRatios.ORIGINAL
        ? { [FALLBACK_WIDTH]: buildUrl(pic.data, { impolicy: 'crop', width: pic.width, height: pic.height }) }
        : pic.responsiveImageLinksData[aspect];
    const widestWidth = aspect === AspectRatios.ORIGINAL ? FALLBACK_WIDTH : getWidestImageWidth(images);

    const srcSet = IMAGE_WIDTHS.filter((item) => item <= this.maxWidthImage()).map((width) => {
      return {
        width,
        src: buildUrl(images[widestWidth], { imwidth: width }),
      };
    });

    const disableCropping = isTruthy(pic.disableCropping) && !!pic.width && !!pic.height;

    const fallbackSrc = buildUrl(images[FALLBACK_WIDTH], { imwidth: FALLBACK_WIDTH });

    return {
      fallbackSrc,
      thumbnailSrc: buildUrl(images[FALLBACK_WIDTH], { imwidth: THUMBNAIL_WIDTH }),
      srcSet,
      hasAspectRatio: aspect !== AspectRatios.ORIGINAL && !disableCropping,
      customAspectRatio: disableCropping ? aspectRatio(pic.width, pic.height) : undefined,
    };
  }

  /**
   * Checks if the pic is invalid as a ResponsiveImage type.
   *
   * This checks that the pic is a ResponsiveImage type,
   * has a ratio and the ResponsiveImage can handle the requested ratio
   */
  private invalidResponsiveImageRatio(pic: ResponsiveImage | StaticImage, ratio: keyof typeof AspectRatios): boolean {
    if (isStaticImage(pic)) {
      return false;
    }

    return (
      !ratio ||
      !(
        (ratio !== 'ORIGINAL' && pic.responsiveImageLinksData && pic.responsiveImageLinksData[AspectRatios[ratio]]) ||
        (ratio === 'ORIGINAL' && pic.data && pic.width && pic.height)
      )
    );
  }
}
